import React, { useState } from "react";
import {
  ActionIcon,
  Box,
  Divider,
  Group,
  SimpleGrid,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { Page } from "../layout/page";
import "to-color-string";

function textMatches(value, query, t) {
  if (!value) return false;
  const source = String(value).toLowerCase();
  const translated = String(t(value)).toLowerCase();
  return source.includes(query) || translated.includes(query);
}

export function filterSettingSections(sections, query, t = (value) => value) {
  const normalized = String(query ?? "").trim().toLowerCase();
  if (!normalized) return sections ?? [];

  return (sections ?? [])
    .map((section) => {
      if (textMatches(section.label, normalized, t)) return section;
      const settings = (section.settings ?? []).filter(
        (setting) =>
          textMatches(setting.label, normalized, t) ||
          textMatches(setting.description, normalized, t)
      );
      return { ...section, settings };
    })
    .filter((section) => section.settings.length > 0);
}

const SettingButton = ({ setting }) => {
  const tabNav = useTabNavigate();
  const { label, description, link } = setting;
  const { t } = useTranslation();
  return (
    <UnstyledButton onClick={() => tabNav.open({ pathname: link })}>
      <Group maw={300} noWrap align={"start"}>
        <ThemeIcon
          size="xl"
          radius="xl"
          variant="light"
          sx={{ backgroundColor: label.toColorString() }}
        >
          <setting.icon size={15} color={"white"} />
        </ThemeIcon>
        <span>
          <Title order={6}> {t(label)} </Title>
          <Text size={"xs"} color="dimmed" lineClamp={2}>
            {t(description)}
          </Text>
        </span>
      </Group>
    </UnstyledButton>
  );
};

const SettingSection = ({ section, spacing = 40, cols = 3 }) => {
  const { label, settings } = section;
  const { t } = useTranslation();

  return (
    <Box mb={40}>
      <Text size={"sm"} weight={500}>
        {t(label)}
      </Text>
      <Divider mb={"xl"} mt="xs" />

      <SimpleGrid cols={cols} spacing={spacing}>
        {settings.map((setting) => (
          <SettingButton key={setting.label} setting={setting} />
        ))}
      </SimpleGrid>
    </Box>
  );
};

const SettingHub = ({ sections }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const visible = filterSettingSections(sections, query, t);

  return (
    <Page title={"Settings"}>
      <TextInput
        placeholder={t("Search settings")}
        icon={<IconSearch size={16} />}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        mb="xl"
        rightSection={
          query ? (
            <ActionIcon
              size="sm"
              variant="transparent"
              onClick={() => setQuery("")}
              aria-label={t("Clear")}
            >
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
      />
      {visible.length === 0 ? (
        <Text size="sm" color="dimmed">
          {t("No settings match")}
        </Text>
      ) : (
        visible.map((section) => (
          <SettingSection key={section.label} section={section} />
        ))
      )}
    </Page>
  );
};

export default SettingHub;
